import React from "react";

interface OKRScoreDisplayProps {
  progress: number;
  score?: number;
  commitmentType?: "committed" | "aspirational";
  showDetailed?: boolean;
}

export const OKRScoreDisplay: React.FC<OKRScoreDisplayProps> = ({
  progress,
  score,
  commitmentType = "committed",
  showDetailed = false,
}) => {
  // Handle NaN and undefined progress
  const safeProgress = isNaN(progress) || progress === undefined ? 0 : progress;

  // Score comes from backend as 0.0-1.0, convert to percentage for display
  // If score is provided and is less than or equal to 1, assume it's a decimal
  const actualScore =
    score !== undefined && score !== null
      ? score <= 1
        ? score * 100
        : score // Convert decimal to percentage if needed
      : safeProgress;

  // Interpret the score based on Google's methodology (actualScore is now 0-100)
  const getScoreInterpretation = () => {
    if (commitmentType === "committed") {
      // Committed OKRs: 100% is required (pass/fail)
      if (actualScore >= 100) {
        return {
          color: "green",
          status: "Complete",
          message: "Committed goal achieved",
        };
      } else if (actualScore >= 70) {
        return {
          color: "yellow",
          status: "Incomplete",
          message: "Close but needs 100%",
        };
      } else if (actualScore >= 40) {
        return {
          color: "yellow",
          status: "At Risk",
          message: "Committed goal at risk",
        };
      } else {
        return {
          color: "red",
          status: "Off Track",
          message: "Committed goal in danger",
        };
      }
    } else {
      // Aspirational OKRs: 60-70% is the target zone
      if (actualScore >= 100) {
        return {
          color: "green",
          status: "Exceptional",
          message: "Exceeded stretch goal!",
        };
      } else if (actualScore >= 70) {
        return {
          color: "green",
          status: "On Track",
          message: "Strong progress",
        };
      } else if (actualScore >= 40) {
        return {
          color: "yellow",
          status: "Good Progress",
          message: "Making progress",
        };
      } else {
        return {
          color: "red",
          status: "Early Stage",
          message: "Still working toward goal",
        };
      }
    }
  };

  const interpretation = getScoreInterpretation();

  // Determine color classes based on interpretation
  const getColorClasses = () => {
    switch (interpretation.color) {
      case "green":
        return {
          bg: "bg-green-100",
          bar: "bg-green-500",
          text: "text-green-700",
          badge: "bg-green-100 text-green-800",
        };
      case "yellow":
        return {
          bg: "bg-yellow-100",
          bar: "bg-yellow-500",
          text: "text-yellow-700",
          badge: "bg-yellow-100 text-yellow-800",
        };
      case "red":
        return {
          bg: "bg-red-100",
          bar: "bg-red-500",
          text: "text-red-700",
          badge: "bg-red-100 text-red-800",
        };
      default:
        return {
          bg: "bg-gray-100",
          bar: "bg-gray-500",
          text: "text-gray-700",
          badge: "bg-gray-100 text-gray-800",
        };
    }
  };

  const colors = getColorClasses();

  if (showDetailed) {
    return (
      <div className="space-y-2">
        {/* Score and Type Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className={`text-lg font-semibold ${colors.text}`}>
              {actualScore.toFixed(0)}%
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors.badge}`}
            >
              {interpretation.status}
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {commitmentType === "committed" ? "Committed" : "Aspirational"}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full">
          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`${colors.bar} h-2 rounded-full transition-all duration-300`}
              style={{ width: `${Math.min(100, Math.max(0, safeProgress))}%` }}
            />
          </div>
        </div>

        {/* Interpretation Message */}
        <p className={`text-xs ${colors.text}`}>{interpretation.message}</p>

        {/* Target Indicator for Aspirational */}
        {commitmentType === "aspirational" && (
          <div className="relative mt-1">
            <div className="flex items-center text-xs text-gray-500">
              <span>Target Zone: 60-70%</span>
              <div className="ml-2 flex-1 h-1 bg-gray-200 rounded relative">
                <div
                  className="absolute h-1 bg-blue-300 rounded"
                  style={{ left: "60%", width: "10%" }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Simple display - just show percentage with color
  return (
    <span className={`text-sm font-medium ${colors.text}`}>
      {Math.round(safeProgress)}%
    </span>
  );
};
